import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TablePagination,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import AssessmentModify from "src/pages/QuizManagement/AssessmentModify";
import Error403 from "../Error/403";
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SchoolIcon from "@mui/icons-material/School";

// Lab type configuration
const LAB_TYPES = {
  left: { key: 'preconfig', label: 'Preconfigured' },
  center: { key: 'custom', label: 'Custom' },
  right: { key: 'hvm', label: 'Hypervised' },
  justify: { key: 'svm', label: 'Shared' },
  trainer: { key: 'cloud', label: 'Cloud' },
  trainer1: { key: 'wise', label: 'Wise' },
  trainer2: { key: 'manual', label: 'Manual' },
  trainer3: { key: 'saas', label: 'SaaS' },
};

const QuizManage = () => {
  const [alignment, setAlignment] = useState("left");
  const [updatedQuizResponse, setUpdatedQuizResponse] = useState([]);
  const [quizResponse, setQuizResponse] = useState([]);
  const [searchitems, setSearchItems] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Separate pagination state for each lab type
  const [pagination, setPagination] = useState(
    Object.keys(LAB_TYPES).reduce((acc, labType) => {
      acc[labType] = { page: 0, rowsPerPage: 10 };
      return acc;
    }, {})
  );

  // Get current pagination for active lab type
  const getCurrentPagination = () => pagination[alignment] || { page: 0, rowsPerPage: 10 };

  const handleLabTypeChange = (event) => {
    const newAlignment = event.target.value;
    setAlignment(newAlignment);
    // Reset search and pagination when changing lab type
    setSearchItems("");
    setPagination(prev => ({
      ...prev,
      [newAlignment]: { page: 0, rowsPerPage: prev[newAlignment]?.rowsPerPage || 10 }
    }));
  };

  const handleSearchChange = useCallback((e) => {
    setSearchItems(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchItems("");
  }, []);

  // Fetch quiz data
  const fetchQuizData = useCallback(async () => {
    const labType = LAB_TYPES[alignment]?.key;
    if (!labType) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/editquizview/${labType}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('token'),
          },
        }
      );

      if (!response.ok) throw new Error('Network response was not ok');
      
      const data = await response.json();
      if (!data.detail) {
        const validStatuses = [
          'Creating', 'Stopping', 'Starting', 'Started', 'Cordoned',
          'Start Initiated', 'Stopped', 'Stop Initiated', 'Running'
        ];
        const activeInstances = data.filter(item => 
          validStatuses.includes(item.status)
        );
        setQuizResponse(activeInstances);
      } else {
        setQuizResponse([]);
      }
    } catch (err) {
      setError('Failed to load assignments. Please try again.');
      setQuizResponse([]);
    } finally {
      setLoading(false);
    }
  }, [alignment]);

  useEffect(() => {
    fetchQuizData();
  }, [alignment, updatedQuizResponse, fetchQuizData]);

  // Memoized filtered and paginated data
  const filteredQuizList = useMemo(() => {
    const term = (searchitems || "").toLowerCase();
    return quizResponse.filter((quiz) =>
      quiz.lab_Name?.toLowerCase().includes(term) ||
      quiz.name?.toLowerCase().includes(term) ||
      quiz.assignment_name?.toLowerCase().includes(term)
    );
  }, [quizResponse, searchitems]);

  const { page, rowsPerPage } = getCurrentPagination();
  const paginatedQuizList = useMemo(() => {
    return filteredQuizList.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredQuizList, page, rowsPerPage]);

  // Summary stats
  const stats = useMemo(() => ({
    total: quizResponse.length,
    filtered: filteredQuizList.length,
    currentLabType: LAB_TYPES[alignment]?.label || '',
  }), [quizResponse.length, filteredQuizList.length, alignment]);

  const handleChangePage = (event, newPage) => {
    setPagination(prev => ({
      ...prev,
      [alignment]: {
        ...prev[alignment],
        page: newPage
      }
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPagination(prev => ({
      ...prev,
      [alignment]: {
        page: 0, // Reset to first page when changing rows per page
        rowsPerPage: newRowsPerPage
      }
    }));
  };

  // Reset pagination when search changes
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      [alignment]: {
        ...prev[alignment],
        page: 0
      }
    }));
  }, [searchitems, alignment]);

  // DataGrid columns (memoized)
  const columns = useMemo(() => [
    { 
      field: 'id', 
      headerName: 'Lab Id', 
      flex: 0.6, 
      minWidth: 80, 
      headerAlign: 'center', 
      align: 'center' 
    },
    { 
      field: 'labName', 
      headerName: 'Lab Name', 
      flex: 1, 
      minWidth: 180, 
      headerAlign: 'center', 
      align: 'center' 
    },
    { 
      field: 'assessmentName', 
      headerName: 'Assessment Name', 
      flex: 1, 
      minWidth: 220, 
      headerAlign: 'center', 
      align: 'center' 
    },
    { 
      field: 'assignmentName', 
      headerName: 'Assignment Name', 
      flex: 1, 
      minWidth: 220, 
      headerAlign: 'center', 
      align: 'center' 
    },
    { 
      field: 'assessmentDate', 
      headerName: 'Assessment Date', 
      flex: 1, 
      minWidth: 160, 
      headerAlign: 'center', 
      align: 'center' 
    },
    {
      field: 'edit',
      headerName: 'Edit',
      flex: 0.6,
      minWidth: 70,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <AssessmentModify
          LabID={params.row.id}
          AssessmentName={params.row.assessmentName}
          AssignmentName={params.row.assignmentName}
          AssessmentDate={params.row.assessmentDate}
          LabType={alignment}
          OrganizationType={params.row.organization_type}
          StartDate={params.row.start_date}
          EndDate={params.row.end_date}
          onModified={setUpdatedQuizResponse}
        />
      )
    }
  ], [alignment]);

  // Check permissions
  const hasPermission = !(
    localStorage.getItem("user_access") === "You do not have permission to perform this action." &&
    localStorage.getItem("group") !== "trainer" && 
    localStorage.getItem("group") !== "admin"
  );

  if (!hasPermission) {
    return <Error403 />;
  }

  return (
    <>
      <Helmet>
        <title>LabAsService - Manage Assessment Assignments</title>
      </Helmet>
      {!hasPermission ? (
        <Error403 />
      ) : (
        <Container maxWidth={false} sx={{ py: 6, pb: 8, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
          <Card sx={{ mb: 4 }}>
            {/* Hero Header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, rgba(46,125,50,0.12), rgba(21,101,192,0.12))',
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                p: { xs: 2.5, md: 3 },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <EditIcon color="primary" sx={{ fontSize: 32 }} />
                <Box>
                  <Typography variant="h4" component="h1">
                    Manage Assessment Assignments
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View and edit assessment assignments for {stats.currentLabType.toLowerCase()} labs
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Divider />

            {/* Summary Metrics */}
            <Grid container spacing={1.5} sx={{ px: { xs: 2.5, md: 3 }, py: 1.5 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack spacing={0.5} alignItems="flex-start">
                      <AssignmentIcon color="primary" sx={{ fontSize: 28 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                        Total Assignments
                      </Typography>
                      {loading ? (
                        <Skeleton variant="text" width={50} height={32} />
                      ) : (
                        <Typography variant="h5" sx={{ fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.2 }}>
                          {stats.total}
                        </Typography>
                      )}
                      <Chip 
                        label={stats.currentLabType} 
                        color="primary" 
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem', mt: 0.5 }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack spacing={0.5} alignItems="flex-start">
                      <SchoolIcon color="info" sx={{ fontSize: 28 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                        Visible Assignments
                      </Typography>
                      {loading ? (
                        <Skeleton variant="text" width={50} height={32} />
                      ) : (
                        <Typography variant="h5" sx={{ fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.2 }}>
                          {stats.filtered}
                        </Typography>
                      )}
                      {searchitems && (
                        <Chip 
                          label={`${stats.filtered} found`} 
                          color="info" 
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem', mt: 0.5 }}
                        />
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Divider />

            {/* Controls Section */}
            <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
              >
                {/* Lab Type Selector */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: { sm: 250 } }}>
                  <Typography
                    sx={{
                      whiteSpace: 'nowrap',
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: 'text.primary',
                    }}
                  >
                    Lab Type
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <Select
                      value={alignment}
                      onChange={handleLabTypeChange}
                      sx={{
                        backgroundColor: 'white',
                        '& .MuiSelect-select': {
                          py: 1,
                        },
                      }}
                    >
                      {Object.entries(LAB_TYPES).map(([value, config]) => (
                        <MenuItem key={value} value={value}>
                          {config.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Search Field */}
                <TextField
                  placeholder="Search by lab name, assessment, or assignment"
                  value={searchitems}
                  onChange={handleSearchChange}
                  size="small"
                  sx={{
                    width: '100%',
                    maxWidth: { xs: '100%', sm: 400, md: 500 },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchitems && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={handleClearSearch}
                          aria-label="clear search"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* Data Grid Section */}
              <Box
                sx={{
                  width: '100%',
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {loading ? (
                  <Box sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      {[1, 2, 3].map((i) => (
                        <Skeleton key={i} variant="rectangular" height={60} />
                      ))}
                    </Stack>
                  </Box>
                ) : filteredQuizList.length > 0 ? (
                  <>
                    <DataGrid
                      autoHeight
                      rows={paginatedQuizList.map((instance) => ({
                        id: instance.lab_id,
                        labName: instance.lab_Name,
                        assessmentName: instance.name,
                        assignmentName: instance.assignment_name,
                        assessmentDate: instance.date,
                        organization_type: instance.organization_type,
                        start_date: instance.start_date,
                        end_date: instance.end_date,
                      }))}
                      columns={columns}
                      hideFooter
                      disableColumnMenu
                      sx={{
                        '& .MuiDataGrid-row:hover': {
                          backgroundColor: 'action.hover',
                        },
                        '& .MuiDataGrid-cell': {
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: 'action.hover',
                          borderBottom: '2px solid',
                          borderColor: 'divider',
                          fontWeight: 600,
                        },
                      }}
                    />
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={filteredQuizList.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      sx={{
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        '& .MuiTablePagination-toolbar': {
                          justifyContent: 'flex-start',
                          paddingLeft: 2,
                        },
                        '& .MuiTablePagination-spacer': {
                          display: 'none',
                        },
                        '& .MuiTablePagination-selectLabel': {
                          margin: 0,
                        },
                        '& .MuiTablePagination-displayedRows': {
                          margin: 0,
                        },
                      }}
                    />
                  </>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 300,
                      color: 'text.secondary',
                    }}
                  >
                    <EditIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" gutterBottom>
                      {quizResponse.length === 0 
                        ? 'No assignments available' 
                        : 'No matching assignments found'}
                    </Typography>
                    <Typography variant="body2" align="center" sx={{ maxWidth: 400, mt: 1 }}>
                      {quizResponse.length === 0
                        ? `No assessment assignments found for ${stats.currentLabType.toLowerCase()} labs. Assignments will appear here once created.`
                        : `No assignments match your search "${searchitems}". Try adjusting your search terms.`}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        </Container>
      )}
    </>
  );
};

export default QuizManage;